import head from 'lodash/head';
import get from 'lodash/get';
import dayjs from 'dayjs';

import { ProviderSummary } from 'store/findProvider/types';
import {
  AppointmentDetails,
  AppointmentLocation,
  AppointmentLocationDisplay,
  BookedAppointment,
  GroupedVisitType,
  Slots,
  TimeSlot,
  TimeSlotDisplay,
  VisitType
} from 'store/booking/types';

const getLocationDisplay = (appointment: AppointmentDetails): string =>
  appointment?.appointmentLocation?.locationName ?? '';

export const normalizeAppointment = (appointment: AppointmentDetails) => ({
  appointmentId: appointment.appointmentId
});

export const normalizedAppointments = (appointments: BookedAppointment[] = []) =>
  appointments
    .map(appointment => [
      appointment,
      dayjs(appointment.appointmentDate),
      appointment.providerDetails
    ])
    .map(([appointment, dt, providerDetails = {}]) => ({
      ...appointment,
      day: dt?.format('D'),
      month: dt?.format('MMM'),
      fullMonth: dt?.format('MMMM'),
      year: dt?.format('YYYY'),
      time: dt?.format('h:mm A'),
      locationDisplay: getLocationDisplay(appointment),
      // TODO: get this from backend service - too brittle here
      locationName: head(getLocationDisplay(appointment)?.split('-'))?.trim()
    }))
    .sort((a, b) => a.appointmentDate - b.appointmentDate);

export const normalizeProviderNew = ({
  consumerId,
  corpProvId,
  cernerResourceId,
  providerDisplayName,
  locations,
  specialties
}) => ({
  id: corpProvId,
  resourceId: cernerResourceId,
  patientId: consumerId,
  name: providerDisplayName,
  // TODO: Need Image Url
  imageUrl:
    'https://intermountainhealthcare.org/-/media/images/modules/physician-directory/profiles/a/abbott-j-thaddaeus.jpg?mh=320&mw=320',
  specialties,
  specialty: get(head(specialties), 'nameLong', ''),
  locations,
  // TODO: Need lastVisit
  lastVisit: new Date() // TODO: Still need this field
});

export const normalizeProvidersNew = (providers = []) => providers.map(normalizeProviderNew);

const mapVisitTypeLocations = locationsSet =>
  locationsSet.map(
    ({
      locationId,
      appointmentLocation = {}
    }: AppointmentLocation): AppointmentLocationDisplay => ({
      ...appointmentLocation,
      locationId,
      name: appointmentLocation.locationName
    })
  );

export const normalizedVisitTypes = (visitTypes: VisitType[] = []) =>
  visitTypes
    .map(vt => ({
      id: vt.synonymId,
      name: vt.synonymDisplay,
      // TODO Maybe ask to move this to a top level property of a visit type
      label: get(vt, 'dfdSynonymDisplaySet[0].synonymName') || vt.synonymDisplay,
      visitTypeId: vt.visitTypeId,
      visitTypeCode: vt.visitType,
      synonymId: vt.synonymId,
      commentRequired: vt.isCommentRequired === 'Y',
      specialty: vt.specialty || {},
      specialtyDisplaySet: vt.dfdSynonymDisplaySet || undefined,
      locations: mapVisitTypeLocations(vt.bookingLocationSet || [])
    }))
    .map(vt => ({
      ...vt,
      value: vt
    }));

export const normalizedFhirTimeSlots = (timeSlots: TimeSlot[] = []): TimeSlotDisplay[] =>
  timeSlots.map(ts => ({
    ...ts,
    timeZone: 'MTN',
    datetime: ts.start,
    duration: 20,
    timeDisplay: dayjs(ts.start)
      .format('h:mm a')
      .replace(' am', 'a')
      .replace(' pm', 'p')
  }));

export const normalizedGroupedVisitTypes = (groupedVisitTypes: GroupedVisitType[] = []) =>
  groupedVisitTypes.map(grouped => ({
    appointmentTypes: grouped.appointmentTypes,
    description: grouped.description,
    id: grouped.id,
    insuranceTypes: grouped.insuranceTypes,
    stateLicenseList: grouped.stateLicenseList,
    name: grouped.name,
    visitTypes: grouped.visitTypes.map(vt => ({
      id: vt.synonymId,
      name: vt.synonymDisplay,
      label: get(vt, 'dfdSynonymDisplaySet[0].synonymName') || vt.synonymDisplay,
      visitTypeId: vt.visitTypeId,
      visitTypeCode: vt.visitType,
      synonymId: vt.synonymId,
      commentRequired: vt.isCommentRequired === 'Y',
      specialty: vt.specialty || {},
      specialtyDisplaySet: vt.dfdSynonymDisplaySet || undefined,
      locations: vt.bookingLocationSet.map(location => ({
        locationId: location.locationId,
        locationName: location.locationName,
        addressLine1: location.addressLine1,
        addressLine2: location.addressLine2 || undefined,
        city: location.city,
        state: location.state,
        postalCode: location.postalCode,
        phoneDisplay: location.phoneDisplay,
        slots: location.slots.map(slot => ({
          id: slot.id,
          datetime: slot.start,
          duration: dayjs(slot.end).diff(dayjs(slot.start)),
          timeDisplay: dayjs(slot.start)
            .format('h:mm a')
            .replace(' am', 'a')
            .replace(' pm', 'p')
        }))
      })),
      appointmentType: vt.appointmentType,
      appointmentNameInCerner: vt.appointmentNameInCerner
    }))
  }));

export const normalizedTimeSlots = (timeSlots: TimeSlot[] = []): TimeSlotDisplay[] =>
  timeSlots.map(ts => ({
    ...ts,
    locationId: ts.locationId,
    datetime: ts.consumerStartDate,
    duration: ts.consumerStartDurationMinutes,
    timeDisplay: dayjs(ts.consumerStartDate)
      .format('h:mm a')
      .replace(' am', 'a')
      .replace(' pm', 'p')
  }));

export const normalizedGroupedTimeSlots = (timeSlots: Slots[]): TimeSlotDisplay[] =>
  timeSlots.map(ts => ({
    ...ts,
    datetime: ts.start,
    duration: dayjs(ts.end).diff(dayjs(ts.start)),
    timeDisplay: dayjs(ts.start)
      .format('h:mm a')
      .replace(' am', 'a')
      .replace(' pm', 'p')
  }));

export const isBookableFilter = (dr: ProviderSummary) =>
  dr.isAcceptingOnlineBooking && dr.cernerResourceId;

export const mapDoctorToType = (dr: ProviderSummary, type: 'isPcp' | 'isPreferred' | 'isPast') => ({
  ...dr,
  [type]: true
});
